import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { theme } from '../theme'

// Mirrors CLICK_IDS_STORE_KEY in @chilipiper/web-data/src/tracking/clientIds.ts. Not imported —
// the kitchensink talks to the snippet through globals only, like a customer page would.
const CLICK_IDS_STORE_KEY = 'chilitracking:click-ids'

// Deterministic platform-tag cookie values for manually exercising the cookie fallback path.
const PLATFORM_COOKIES: ReadonlyArray<readonly [string, string]> = [
  ['li_fat_id', 'li-from-cookie'],
  ['_gcl_aw', 'GCL.1712345678.gclid-from-cookie'],
  ['_fbc', 'fb.1.1712345678000.fbclid-from-cookie'],
  ['_fbp', 'fb.1.1712345678000.987654321'],
  ['_ga', 'GA1.1.708647412.1712345678'],
  ['_ga_KITCHEN1', 'GS1.1.1755612000.5.1.1755612300.60.0.0'],
]

// How long the fire button waits for a track request to show up in resource timing before it
// reports that nothing was sent (the usual cause: the snippet was never initialized).
const FIRE_TIMEOUT_MS = 6_000

const JITSU_REQUEST_PATTERN = /\/api\/s\/([a-z]+)/

const sectionTitle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  color: theme.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  margin: '0 0 16px',
}

const card: CSSProperties = {
  backgroundColor: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: '10px',
  padding: '20px 24px',
  marginBottom: '20px',
}

const buttonStyle: CSSProperties = {
  backgroundColor: theme.accent,
  color: '#fff',
  border: 'none',
  borderRadius: '7px',
  padding: '8px 18px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const preStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'monospace',
  fontSize: '12px',
  color: theme.text,
  backgroundColor: theme.surfaceElevated,
  borderRadius: '8px',
  padding: '14px 16px',
  overflowX: 'auto',
  lineHeight: 1.6,
}

const code = (text: string) => (
  <code
    style={{
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: theme.accent,
      backgroundColor: theme.accentSubtle,
      padding: '1px 5px',
      borderRadius: '3px',
    }}
  >
    {text}
  </code>
)

// Inline replacement for the design-system Spinner (not available in this repo).
const Spinner = () => (
  <>
    <style>{'@keyframes ks-spin { to { transform: rotate(360deg) } }'}</style>
    <span
      aria-hidden
      style={{
        width: '12px',
        height: '12px',
        flexShrink: 0,
        borderRadius: '50%',
        border: `2px solid ${theme.accentSubtle}`,
        borderTopColor: theme.accent,
        display: 'inline-block',
        animation: 'ks-spin 0.8s linear infinite',
      }}
    />
  </>
)

type StepProps = { n: number; children: React.ReactNode }

const Step = ({ n, children }: StepProps) => (
  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
    <div
      style={{
        flexShrink: 0,
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: theme.accentSubtle,
        border: `1px solid ${theme.accent}`,
        color: theme.accent,
        fontSize: '11px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {n}
    </div>
    <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
)

type StatusTone = 'pending' | 'ok' | 'warn'

const statusToneStyles: Record<StatusTone, CSSProperties> = {
  pending: {
    color: theme.accent,
    backgroundColor: theme.accentSubtle,
    border: `1px solid ${theme.accent}`,
  },
  ok: {
    color: theme.success,
    backgroundColor: theme.successSubtle,
    border: '1px solid rgba(34, 197, 94, 0.2)',
  },
  warn: {
    color: '#f59e0b',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    border: '1px solid rgba(245, 158, 11, 0.2)',
  },
}

type StatusLineProps = { testId: string; tone: StatusTone; children: React.ReactNode }

const StatusLine = ({ testId, tone, children }: StatusLineProps) => (
  <div
    data-test-id={testId}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      padding: '6px 12px',
      borderRadius: '6px',
      ...statusToneStyles[tone],
    }}
  >
    {children}
  </div>
)

const readPersistedClickIds = (): string => {
  try {
    const raw = localStorage.getItem(CLICK_IDS_STORE_KEY)
    if (!raw) return 'none'
    try {
      return JSON.stringify(JSON.parse(raw), null, 2)
    } catch {
      return raw
    }
  } catch {
    return 'unavailable'
  }
}

const jitsuTypeFromUrl = (url: string): string | undefined => JITSU_REQUEST_PATTERN.exec(url)?.[1]

type ObservedRequest = { type: string; atLabel: string }

type FireStatus =
  | { state: 'idle' }
  | { state: 'firing' }
  | { state: 'fired'; atLabel: string }
  | { state: 'no-request' }

export const ClickIdsPage = () => {
  const [persisted, setPersisted] = useState(readPersistedClickIds)
  const [persistedChanged, setPersistedChanged] = useState(false)
  const prevPersistedRef = useRef(persisted)

  // Requests the jitsu snippet actually sent from this page, observed through resource timing.
  // This is what makes the button feedback honest: "fired" means a request left the browser,
  // not just that the click handler ran.
  const [observedRequests, setObservedRequests] = useState<ObservedRequest[]>([])
  const [trackCount, setTrackCount] = useState(0)

  const [fireStatus, setFireStatus] = useState<FireStatus>({ state: 'idle' })
  const fireBaselineRef = useRef<number | null>(null)
  const fireTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [cookieStatus, setCookieStatus] = useState<string | null>(null)
  const [clearStatus, setClearStatus] = useState<string | null>(null)

  useEffect(() => {
    const interval = setInterval(() => setPersisted(readPersistedClickIds()), 500)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (prevPersistedRef.current === persisted) return
    prevPersistedRef.current = persisted
    setPersistedChanged(true)
    const timeout = setTimeout(() => setPersistedChanged(false), 1_500)
    return () => clearTimeout(timeout)
  }, [persisted])

  useEffect(() => {
    const record = (entries: PerformanceEntryList) => {
      const hits = entries
        .map(entry => ({
          type: jitsuTypeFromUrl(entry.name),
          atLabel: new Date(performance.timeOrigin + entry.startTime).toLocaleTimeString(),
        }))
        .filter((hit): hit is ObservedRequest => Boolean(hit.type))
      if (hits.length === 0) return
      setObservedRequests(prev => [...hits].reverse().concat(prev).slice(0, 8))
      const tracks = hits.filter(hit => hit.type === 'track').length
      if (tracks > 0) {
        setTrackCount(prev => prev + tracks)
      }
    }

    record(performance.getEntriesByType('resource'))
    const observer = new PerformanceObserver(list => record(list.getEntries()))
    observer.observe({ type: 'resource', buffered: false })
    return () => observer.disconnect()
  }, [])

  // Resolve the fire loader as soon as a new track request shows up.
  useEffect(() => {
    if (fireStatus.state !== 'firing') return
    if (fireBaselineRef.current === null || trackCount <= fireBaselineRef.current) return
    if (fireTimeoutRef.current) clearTimeout(fireTimeoutRef.current)
    setFireStatus({ state: 'fired', atLabel: new Date().toLocaleTimeString() })
  }, [trackCount, fireStatus.state])

  useEffect(
    () => () => {
      if (fireTimeoutRef.current) clearTimeout(fireTimeoutRef.current)
    },
    []
  )

  const fireEvent = () => {
    if (fireStatus.state === 'firing') return
    fireBaselineRef.current = trackCount
    setFireStatus({ state: 'firing' })
    window.ChiliWebData?.productImpression('Chat')
    fireTimeoutRef.current = setTimeout(() => {
      setFireStatus(prev => (prev.state === 'firing' ? { state: 'no-request' } : prev))
    }, FIRE_TIMEOUT_MS)
  }

  const countPlatformCookies = () =>
    PLATFORM_COOKIES.filter(([name]) => document.cookie.includes(`${name}=`)).length

  const setPlatformCookies = () => {
    for (const [name, value] of PLATFORM_COOKIES) {
      document.cookie = `${name}=${value}; path=/`
    }
    setClearStatus(null)
    setCookieStatus(`✓ ${countPlatformCookies()}/${PLATFORM_COOKIES.length} platform cookies set`)
  }

  const clearClickIdState = () => {
    for (const [name] of PLATFORM_COOKIES) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    }
    try {
      localStorage.removeItem(CLICK_IDS_STORE_KEY)
    } catch {
      // localStorage can be unavailable — nothing to clear then
    }
    setPersisted(readPersistedClickIds())
    setCookieStatus(null)
    const remaining = countPlatformCookies()
    setClearStatus(
      remaining === 0
        ? '✓ Cookies removed and persisted ids cleared'
        : `⚠ ${remaining} platform cookies still present`
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <span
          style={{
            fontSize: '10px',
            fontWeight: 700,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            backgroundColor: theme.accentSubtle,
            padding: '3px 8px',
            borderRadius: '4px',
          }}
        >
          Tracking context
        </span>
      </div>

      <h1
        style={{
          fontSize: '24px',
          fontWeight: 700,
          color: theme.text,
          margin: '0 0 6px',
          letterSpacing: '-0.02em',
        }}
      >
        Ad Click IDs
      </h1>
      <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 28px' }}>
        The tracker captures the click ids ad platforms append to ad-click landings —{' '}
        {code('li_fat_id')} (LinkedIn), {code('gclid')} / {code('gbraid')} / {code('wbraid')}{' '}
        (Google), {code('fbclid')} (Meta) — with the platform tags&apos; cookies (
        {code('li_fat_id')}, {code('_gcl_aw')}, {code('_fbc')} + {code('_fbp')}) as fallback, plus
        the GA4 ids from the {code('_ga')} cookies. Captured ids are persisted and attached to every
        event as {code('context.clientIds')}.
      </p>

      {/* Controls */}
      <div style={card}>
        <p style={sectionTitle}>Controls</p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
            flexWrap: 'wrap',
          }}
        >
          <button
            data-test-id='set-platform-cookies'
            onClick={setPlatformCookies}
            style={buttonStyle}
          >
            Set platform cookies
          </button>

          <button
            data-test-id='fire-click-ids-event'
            onClick={fireEvent}
            disabled={fireStatus.state === 'firing'}
            style={{
              ...buttonStyle,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: fireStatus.state === 'firing' ? 0.7 : 1,
              cursor: fireStatus.state === 'firing' ? 'wait' : 'pointer',
            }}
          >
            {fireStatus.state === 'firing' ? 'Firing…' : 'Fire ProductImpression'}
          </button>

          <button
            data-test-id='clear-click-id-state'
            onClick={clearClickIdState}
            style={{
              ...buttonStyle,
              backgroundColor: theme.surfaceElevated,
              color: theme.text,
              border: `1px solid ${theme.border}`,
            }}
          >
            Clear cookies &amp; persisted ids
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: '20px',
            minHeight: '30px',
          }}
        >
          {cookieStatus && (
            <StatusLine testId='set-cookies-status' tone='ok'>
              {cookieStatus}
            </StatusLine>
          )}
          {clearStatus && (
            <StatusLine testId='clear-status' tone={clearStatus.startsWith('✓') ? 'ok' : 'warn'}>
              {clearStatus}
            </StatusLine>
          )}
          {fireStatus.state === 'firing' && (
            <StatusLine testId='fire-click-ids-status' tone='pending'>
              <Spinner />
              Waiting for the track request to leave the browser…
            </StatusLine>
          )}
          {fireStatus.state === 'fired' && (
            <StatusLine testId='fire-click-ids-status' tone='ok'>
              ✓ Track request sent at {fireStatus.atLabel} — inspect it under{' '}
              <span style={{ fontFamily: 'ui-monospace, monospace' }}>api/s/track</span>
            </StatusLine>
          )}
          {fireStatus.state === 'no-request' && (
            <StatusLine testId='fire-click-ids-status' tone='warn'>
              ✗ No track request seen — initialize the snippet first (Initialize button above)
            </StatusLine>
          )}
        </div>

        <p style={{ fontSize: '12px', color: theme.textMuted, margin: '0 0 6px' }}>
          Persisted click ids ({code(CLICK_IDS_STORE_KEY)}):
        </p>
        <pre
          data-test-id='persisted-click-ids'
          style={{
            ...preStyle,
            border: `1px solid ${persistedChanged ? theme.accent : theme.border}`,
            transition: 'border-color 0.3s',
            marginBottom: '20px',
          }}
        >
          {persisted}
        </pre>

        <p style={{ fontSize: '12px', color: theme.textMuted, margin: '0 0 6px' }}>
          Snippet requests seen on this page (via resource timing):
        </p>
        <div data-test-id='observed-jitsu-requests' style={{ ...preStyle, padding: '10px 16px' }}>
          {observedRequests.length === 0 ? (
            <span style={{ color: theme.textDim }}>
              none yet — initialize the snippet, then navigate or fire events
            </span>
          ) : (
            observedRequests.map((request, index) => (
              <div key={`${request.atLabel}-${request.type}-${index}`}>
                {request.atLabel} · api/s/{request.type}
              </div>
            ))
          )}
        </div>
      </div>

      {/* How to test */}
      <div style={card}>
        <p style={sectionTitle}>How to test</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Step n={1}>
            Add click-id params to the URL — append{' '}
            {code('&li_fat_id=test-li&gclid=test-gclid&fbclid=test-fbclid')} — and reload.
          </Step>
          <Step n={2}>
            Initialize the snippet. Open DevTools → Network and inspect the {code('api/s/page')}{' '}
            request: {code('context.clientIds')} carries the ids ({code('fbclid')} lands as a
            synthesized {code('fbc')}).
          </Step>
          <Step n={3}>
            The <strong style={{ color: theme.text }}>Persisted click ids</strong> box above fills
            in — the ids now ride on every later event, even after the params leave the URL.
          </Step>
          <Step n={4}>
            Click <strong style={{ color: theme.text }}>Fire ProductImpression</strong> and wait for
            the status to confirm the track request left the browser, then check that the{' '}
            {code('api/s/track')} request carries the same {code('context.clientIds')}.
          </Step>
          <Step n={5}>
            Cookie fallback: click{' '}
            <strong style={{ color: theme.text }}>Clear cookies &amp; persisted ids</strong>, remove
            the params from the URL, reload and initialize, then click{' '}
            <strong style={{ color: theme.text }}>Set platform cookies</strong> and fire the event —
            the ids (and {code('ga4')}) now come from the cookies.
          </Step>
        </div>
      </div>

      {/* Event context shape */}
      <div style={card}>
        <p style={sectionTitle}>Event context shape</p>
        <pre style={preStyle}>{`context: {
  clientIds: {
    li_fat_id: '…',            // LinkedIn, URL param or Insight Tag cookie
    gclid: '…',                // Google, URL param or _gcl_aw cookie
    gbraid: '…', wbraid: '…',  // Google (iOS), URL params
    fbc: 'fb.1.<ts>.<fbclid>', // Meta, synthesized from fbclid or _fbc cookie
    fbp: '…',                  // Meta browser id, _fbp cookie
    ga4: { clientId: '…', sessionIds: { <container>: '…' } },
  },
}`}</pre>
      </div>
    </div>
  )
}
